import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { cn } from '@/utils'

import { OrgToolsModal } from '@/components/OrgToolsModal'
import { TableCell } from '@/components/ui/table'
import {
  useUpdateTable,
  useCreateTable,
  useDeleteTable,
  TableColumn,
  TableRow,
  Table,
  TemplateAttribute,
} from '@/services/hooks/template_attributes'
import type { FlattenedDocumentPage, Tool } from '@/lib/api/client'
import { EditableTable, createNewColumn } from './EditableTable'
import {
  ToolSuggestionsSelect,
  type ToolSuggestionItem,
} from './ToolSuggestionsSelect'
import { useDocumentState } from '@/state/document'
import { useRoute } from 'wouter'
import queryClient from '@/queryClient'
import QUERY_KEYS from '@/services/queries/tools/queryKeys'

export type ToolsTableData = {
  toolName: string
  [accessor: string]: any
}

export type ToolsTableContentProps = {
  template?: TemplateAttribute<Table>
  documentPage: FlattenedDocumentPage
  isReadOnly?: boolean
  dataType: 'tools-table'
  tools: Tool[]
  documentVersionToolIds?: Set<string>
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

const getColumnAccessor = (column: TableColumn) => Object.keys(column || {})[0]

const dedupeColumnsByAccessor = (columns: TableColumn[] = []) => {
  const seenAccessors = new Set<string>()

  return columns.filter((column) => {
    const accessor = getColumnAccessor(column)

    if (!accessor || seenAccessors.has(accessor)) {
      return false
    }

    seenAccessors.add(accessor)
    return true
  })
}

export const ToolsTableContent = ({
  template,
  documentPage,
  isReadOnly = false,
  dataType,
  tools,
  documentVersionToolIds,
  onNudgeStart,
  onNudgeEnd,
}: ToolsTableContentProps) => {
  const [isPrintPage] = useRoute(/print$/)
  const draggableId = useMemo(
    () => template?.id || dataType,
    [template?.id, dataType],
  )

  const initialTableData = useMemo<Array<ToolsTableData>>(
    () => [
      {
        toolName: '',
      },
    ],
    [],
  )

  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)
  const [tableData, setTableData] = useState<ToolsTableData[]>(() => [])
  const tableDataRef = useRef<ToolsTableData[]>([])
  const [columnsDef, setColumnsDef] = useState<ColumnDef<ToolsTableData>[]>([])
  const [columnsData, setColumnsData] = useState({})
  const [toolModalState, setToolModalState] = useState<{
    isOpen: boolean
    toolId: string
    toolName: string
  }>({
    isOpen: false,
    toolId: '',
    toolName: '',
  })

  const closePlaceholderModal = useCallback(() => {
    setToolModalState({
      isOpen: false,
      toolId: '',
      toolName: '',
    })
  }, [])

  useEffect(() => {
    tableDataRef.current = tableData
  }, [tableData])

  const openPlaceholderModal = useCallback(
    ({ toolId, toolName }: { toolId: string; toolName: string }) => {
      setToolModalState({
        isOpen: true,
        toolId,
        toolName,
      })
    },
    [],
  )

  const { mutateAsync: updateTable } = useUpdateTable()
  const { mutateAsync: createTable } = useCreateTable()
  const { mutateAsync: deleteTable } = useDeleteTable()

  const saveTableData = useCallback(
    async ({
      columns,
      rows,
    }: {
      columns?: Array<TableColumn>
      rows?: Array<TableRow>
    }) => {
      if (template?.id) {
        const tableData: Table = {
          type: dataType,
        }

        if (columns) {
          tableData.columns = columns
        }

        if (rows) {
          tableData.rows = rows
        }

        await updateTable({
          templateAttributeId: template?.id,
          documentPageId: documentPage.id as string,
          tableData,
        })
        return
      }

      await createTable({
        documentPageId: documentPage.id as string,
        tableData: {
          type: dataType,
          columns,
          rows,
        },
      })
    },
    [documentPage.id, dataType, template?.id, updateTable, createTable],
  )

  const toolSuggestionItems = useMemo<Array<ToolSuggestionItem>>(() => {
    /**
     * Canonical options come from the tools API and are deduplicated by id.
     * We need stable ids so selecting a tool can attach `toolId` to the row.
     */
    const toolsById = new Map<string, ToolSuggestionItem>()
    tools.forEach((tool) => {
      if (!tool?.id || !tool?.name) return
      if (!toolsById.has(tool.id)) {
        toolsById.set(tool.id, {
          id: tool.id,
          name: tool.name,
        })
      }
    })
    return Array.from(toolsById.values())
  }, [tools])

  const selectToolForRow = useCallback(
    async ({
      rowIndex,
      selectedTool,
    }: {
      rowIndex: number
      selectedTool: ToolSuggestionItem
    }) => {
      let nextRows: ToolsTableData[] = []
      setTableData((prevRows) => {
        nextRows = prevRows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                toolName: selectedTool.name,
                toolId: selectedTool.id,
              }
            : row,
        )
        tableDataRef.current = nextRows
        return nextRows
      })

      // Persist immediately once a suggestion is selected.
      await saveTableData({
        rows: nextRows,
      })

      /**
       * Selecting a tool row changes document-version tool membership.
       * Refresh tools queries so notes/table validity pills update immediately.
       */
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TOOLS],
        exact: false,
      })
    },
    [saveTableData],
  )

  const initialColumns = useMemo<Record<string, ColumnDef<ToolsTableData>>>(
    () => ({
      toolName: {
        accessorKey: 'toolName',
        header: 'Tools',
        size: 200,
        cell: ({ getValue, row, column }) => {
          const rowData = row.original as {
            toolId?: string
            toolName?: string
          }
          const displayToolName = getValue<string>() || ''
          const hasToolId =
            typeof rowData.toolId === 'string' && rowData.toolId.trim() !== ''
          const hasDisplayToolName = displayToolName.trim() !== ''
          const accessor = (column.columnDef as any).accessorKey as string
          const isOrgTool =
            hasToolId &&
            Boolean(
              documentVersionToolIds
                ? documentVersionToolIds.has(rowData.toolId as string)
                : tools.find((tool) => tool.id === rowData.toolId),
            )

          return (
            <TableCell
              className="flex flex-1 items-center border-x border-solid border-gray-200 border-collapse text-sm py-1 min-h-6"
              data-id-column={accessor}
              data-id-row={row.index}
            >
              {hasToolId ? (
                <div className="inline-flex items-center">
                  <span
                    className={cn('slash-command-pill transition-colors mx-1', {
                      /**
                       * Print invariant: tool pills should be visually flat in print output.
                       * We always force white background on /print regardless of tool status.
                       */
                      'bg-white': isPrintPage,
                      'bg-[#E5D5FF] hover:bg-[#D9C1FF]':
                        !isPrintPage && isOrgTool && !isReadOnly,
                      'bg-gray-200': !isPrintPage && !isOrgTool,
                    })}
                    data-id-column={accessor}
                    data-id-row={row.index}
                    style={{
                      // Non-org / missing-id tools should not look interactive.
                      cursor: isOrgTool && !isReadOnly ? 'pointer' : 'default',
                    }}
                    onMouseDown={(event) => {
                      // Prevent bubbling into table drag listeners.
                      event.stopPropagation()
                    }}
                    onPointerDown={(event) => {
                      // Prevent bubbling into table drag listeners.
                      event.stopPropagation()
                    }}
                    onClick={(event) => {
                      // Keep click-target scoped to the pill text only.
                      event.stopPropagation()
                      /**
                       * Invariant: editor modal only opens for canonical org tools.
                       * Grey pills represent stale/unresolved values and are view-only.
                       */
                      if (!isOrgTool || isReadOnly || isPrintPage) {
                        return
                      }
                      openPlaceholderModal({
                        toolId: rowData.toolId as string,
                        toolName:
                          typeof rowData.toolName === 'string'
                            ? rowData.toolName
                            : '',
                      })
                    }}
                  >
                    <span>{displayToolName}</span>
                  </span>
                </div>
              ) : isReadOnly || isPrintPage ? (
                hasDisplayToolName ? (
                  <span className="mx-1">{displayToolName}</span>
                ) : null
              ) : (
                <ToolSuggestionsSelect
                  value={displayToolName}
                  items={toolSuggestionItems}
                  disabled={isReadOnly}
                  onSelect={(selectedTool) => {
                    void selectToolForRow({
                      rowIndex: row.index,
                      selectedTool,
                    })
                  }}
                />
              )}
            </TableCell>
          )
        },
      },
    }),
    [
      isReadOnly,
      isPrintPage,
      openPlaceholderModal,
      selectToolForRow,
      toolSuggestionItems,
      tools,
      documentVersionToolIds,
    ],
  )

  const toolsColumns = useMemo(
    () => dedupeColumnsByAccessor(template?.template_values.columns),
    [template?.template_values.columns],
  )

  const toolsRows = useMemo(() => {
    const tmplToolsRows = template?.template_values.rows || []
    return tmplToolsRows.map((row) => {
      const tool = tools.find((t) => t.id === row.toolId)
      return {
        ...row,
        toolName: tool?.name || row.toolName,
      }
    })
  }, [template, tools])

  const tablePosition = useMemo(
    () => template?.template_values.position,
    [template?.template_values.position],
  )
  const columnsSize = useMemo(
    () => template?.template_values.columnsSize,
    [template?.template_values.columnsSize],
  )
  const isManual = useMemo(
    () => template?.template_values.isManual,
    [template?.template_values.isManual],
  )

  const toolSuggestions = useMemo(
    () =>
      // Suggestions should come from the canonical tools API list.
      tools.reduce((accum, tool) => {
        if (!tool?.name || accum.includes(tool.name)) {
          return accum
        }
        accum.push(tool.name)
        return accum
      }, [] as string[]),
    [tools],
  )

  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )

  const selectTable = useCallback(() => {
    setSelectedElement({
      documentPageId: documentPage.id as string,
      id: draggableId,
      type: dataType,
    })
  }, [documentPage.id, draggableId, setSelectedElement])

  useEffect(() => {
    if (!tableData.length) {
      setTableData(() => [...initialTableData])
    }
  }, [tableData])

  useEffect(() => {
    if (toolsColumns) {
      setColumnsData((prev) => ({
        ...prev,
        ...toolsColumns.reduce(
          (acc, column) => ({
            ...acc,
            ...column,
          }),
          {},
        ),
      }))

      toolsColumns.forEach((column) => {
        Object.keys(column).forEach((accessorKey) => {
          const columnDef =
            initialColumns[accessorKey] ??
            (createNewColumn({
              accessorKey,
              onCellChange,
              selectTable,
              isReadOnly,
              documentPageId: documentPage.id as string,
              tableType: 'tools-table',
            }) as ColumnDef<ToolsTableData>)
          setColumnsDef((prev) => [...prev, columnDef])
        })
      })
    }

    return () => {
      setColumnsDef([])
    }
  }, [toolsColumns, initialColumns])

  useEffect(() => {
    if (Array.isArray(toolsRows)) {
      setTableData(() => [...(toolsRows as Array<ToolsTableData>)])
    }

    return () => {
      setTableData([])
    }
  }, [toolsRows])

  const onCellChange = useCallback(
    (rowIndex, columnId, value) => {
      setTableData((prev) =>
        prev.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row,
        ),
      )
    },
    [setTableData],
  )

  const onHeaderChange = useCallback((value: string, accessorKey: string) => {
    setColumnsData((prev) => ({
      ...prev,
      [accessorKey]: value,
    }))
  }, [])

  const saveToolsTableData = useCallback(async () => {
    const cleanColumns = columnsDef.reduce((accum, colDef: any) => {
      const columnAccessor = Object.keys(columnsData).find(
        (accessorKey) => accessorKey === colDef.accessorKey,
      )

      if (!columnAccessor) return accum

      const column = columnsData[columnAccessor]

      return [
        ...accum,
        {
          [columnAccessor]: column,
        },
      ]
    }, [] as Array<any>)
    await saveTableData({
      columns: cleanColumns,
      rows: tableData,
    })
  }, [tableData, columnsData, columnsDef, saveTableData])

  const addNewColumn = useCallback(
    async ({
      accessorKey,
      newColumn,
      position,
    }: {
      accessorKey: string
      newColumn: ColumnDef<any>
      position: 'left' | 'right'
    }) => {
      const newColumnAccessor = (newColumn as { accessorKey: string })
        .accessorKey
      const targetIndex = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )
      const insertIndex = position === 'left' ? targetIndex : targetIndex + 1

      const updatedColumns = [
        ...columnsDef.slice(0, insertIndex),
        { accessorKey: newColumnAccessor },
        ...columnsDef.slice(insertIndex),
      ].map((columnDef: any) => {
        const key = columnDef.accessorKey
        return { [key]: columnsData[key] ?? '' }
      })

      const updatedRows = [
        ...tableData.map((row) => ({
          ...row,
          [newColumnAccessor]: '',
        })),
      ]

      await saveTableData({
        columns: updatedColumns,
        rows: updatedRows,
      })
    },
    [columnsDef, tableData, saveTableData],
  )

  const removeColumn = useCallback(
    async (accessorKey: string) => {
      if (accessorKey && columnsDef.length === 1) {
        void deleteTable({
          templateAttributeId: template?.id as string,
          documentPageId: documentPage.id as string,
        })

        return
      }

      const columnBefore = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )

      if (columnBefore === -1) return
      setColumnsDef((prevColumns) => [
        ...prevColumns.slice(0, columnBefore),
        ...prevColumns.slice(columnBefore + 1),
      ])
      setTableData((prevTableData) =>
        prevTableData.filter((row) => Object.keys(row).includes(accessorKey)),
      )
      setColumnsData((prev) => {
        const newColumns = { ...prev }
        delete newColumns[accessorKey]
        return newColumns
      })

      const filteredToolsRows = tableData
        .map((row) => {
          return Object.keys(row).reduce((acc, currAccessorKey) => {
            if ((currAccessorKey as string) !== accessorKey) {
              acc[currAccessorKey] = row[currAccessorKey]
            }

            return acc
          }, {})
        })
        .filter((row) => Object.keys(row).length)

      const filteredColumns = columnsDef
        .filter(
          (column) => ((column as any).accessorKey as string) !== accessorKey,
        )
        .map((column) => {
          const accessorKey = (column as any).accessorKey
          return { [accessorKey]: columnsData[accessorKey] }
        })

      await saveTableData({
        columns: filteredColumns,
        rows: filteredToolsRows,
      })
    },
    [
      columnsDef,
      tableData,
      saveTableData,
      deleteTable,
      template?.id,
      documentPage.id,
      columnsData,
    ],
  )

  const removeRow = useCallback(
    async (rowIndex) => {
      const filteredRows = tableDataRef.current.filter(
        (_, index) => index !== rowIndex,
      )
      tableDataRef.current = filteredRows
      setTableData(filteredRows)

      await saveTableData({
        rows: filteredRows,
      })
    },
    [saveTableData],
  )

  const addNewRow = useCallback(
    async ({
      rowIndex,
      newRow,
      position,
    }: {
      rowIndex?: number
      newRow: any
      position: 'above' | 'below'
    }) => {
      let insertIndex: number

      if (typeof rowIndex === 'number') {
        insertIndex = position === 'above' ? rowIndex : rowIndex + 1
      } else {
        insertIndex = position === 'above' ? 0 : tableDataRef.current.length
      }

      const updatedRows = [
        ...tableDataRef.current.slice(0, insertIndex),
        newRow,
        ...tableDataRef.current.slice(insertIndex),
      ]
      tableDataRef.current = updatedRows
      setTableData(updatedRows)

      await saveTableData({
        rows: updatedRows,
      })
    },
    [saveTableData],
  )

  useEffect(() => {
    if (columnsSize) {
      setColumnsDef((prevColumns) =>
        prevColumns.map((column) => {
          const accessor = (column as any).accessorKey
          const size = (columnsSize as any)[accessor]
          if (size) {
            return {
              ...column,
              size,
            }
          }
          return column
        }),
      )
    }
  }, [columnsSize])

  const isToolsTableEmpty = tableData.every((row) => !row.toolName)

  if (isReadOnly && isToolsTableEmpty) {
    return null
  }

  return (
    <>
      <EditableTable
        suggestions={{
          toolName: toolSuggestions,
        }}
        saveTableData={saveToolsTableData}
        onCellChange={onCellChange}
        removeRow={removeRow}
        addRow={addNewRow}
        onHeaderChange={onHeaderChange}
        saveCustomColumns={saveToolsTableData}
        removeColumn={removeColumn}
        addColumn={addNewColumn}
        setSelectedColumn={setSelectedColumn}
        setSelectedRow={setSelectedRow}
        selectedRow={selectedRow}
        selectedColumn={selectedColumn}
        documentPage={documentPage}
        tableData={tableData}
        defaultRows={0}
        columnsDef={columnsDef as any}
        columnsData={columnsData}
        notCustomColumns={['toolName']}
        positionData={tablePosition}
        columnsSize={columnsSize || {}}
        tableTemplate={template as any}
        isReadOnly={isReadOnly}
        id="tools-table"
        type="tools-table"
        direction={isManual ? 'left' : 'right'}
        testId="tools-table"
        onNudgeEnd={onNudgeEnd}
        onNudgeStart={onNudgeStart}
      />
      <OrgToolsModal
        open={toolModalState.isOpen}
        onClose={closePlaceholderModal}
        toolId={toolModalState.toolId}
        isReadOnly={isReadOnly}
      />
    </>
  )
}
